/**
 * Ramp Filter Store - Manages quick filter presets and display mode
 *
 * Provides in-memory filtering of aircraft on the ramp view:
 * - Quick presets: Based, Transient, Departing Today
 * - Display mode: Filter (hide non-matching) vs Highlight (dim non-matching)
 * - Highlighted aircraft IDs (from search results)
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { AllocationEnrichedData } from '@/types/aircraft-card'

// ============================================
// Types
// ============================================

export type FilterPreset = 'based' | 'transient' | 'departing_today'
export type FilterDisplayMode = 'filter' | 'highlight'

export interface PresetConfig {
  key: FilterPreset
  label: string
  description: string
}

export const FILTER_PRESETS: PresetConfig[] = [
  { key: 'based', label: 'Based', description: 'Aircraft with active lease contracts' },
  { key: 'transient', label: 'Transient', description: 'Aircraft on transient stays' },
  { key: 'departing_today', label: 'Departing Today', description: 'Transients departing today' },
]

// ============================================
// Filter predicate functions
// ============================================

function isToday(date: Date): boolean {
  const now = new Date()
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  )
}

const PRESET_FILTERS: Record<FilterPreset, (data: AllocationEnrichedData) => boolean> = {
  based: (data) => {
    return !!data.leaseContract && data.leaseContract.status === 'active'
  },
  transient: (data) => {
    return !!data.transientStay
  },
  departing_today: (data) => {
    if (!data.transientStay?.expectedCheckOut) return false
    return isToday(new Date(data.transientStay.expectedCheckOut))
  },
}

// ============================================
// Store
// ============================================

interface RampFilterState {
  activePresets: Set<FilterPreset>
  displayMode: FilterDisplayMode
  highlightedAircraftIds: Set<string>
}

interface RampFilterActions {
  togglePreset: (preset: FilterPreset) => void
  setPreset: (preset: FilterPreset, active: boolean) => void
  clearAllPresets: () => void
  setDisplayMode: (mode: FilterDisplayMode) => void
  setHighlightedAircraft: (ids: Set<string>) => void
  clearHighlights: () => void
  /** Check if an aircraft matches all active presets (OR logic between presets) */
  matchesFilters: (data: AllocationEnrichedData) => boolean
  /** Whether any filters are currently active */
  hasActiveFilters: () => boolean
}

export const useRampFilterStore = create<RampFilterState & RampFilterActions>()(
  devtools(
    (set, get) => ({
      activePresets: new Set<FilterPreset>(),
      displayMode: 'highlight',
      highlightedAircraftIds: new Set<string>(),

      togglePreset: (preset) =>
        set((state) => {
          const next = new Set(state.activePresets)
          if (next.has(preset)) {
            next.delete(preset)
          } else {
            next.add(preset)
          }
          return { activePresets: next }
        }),

      setPreset: (preset, active) =>
        set((state) => {
          const next = new Set(state.activePresets)
          if (active) {
            next.add(preset)
          } else {
            next.delete(preset)
          }
          return { activePresets: next }
        }),

      clearAllPresets: () =>
        set({ activePresets: new Set(), highlightedAircraftIds: new Set() }),

      setDisplayMode: (mode) => set({ displayMode: mode }),

      setHighlightedAircraft: (ids) => set({ highlightedAircraftIds: ids }),

      clearHighlights: () => set({ highlightedAircraftIds: new Set() }),

      matchesFilters: (data) => {
        const { activePresets } = get()
        if (activePresets.size === 0) return true

        // OR logic: aircraft matches if it satisfies ANY active preset
        for (const preset of activePresets) {
          if (PRESET_FILTERS[preset](data)) return true
        }
        return false
      },

      hasActiveFilters: () => {
        const { activePresets, highlightedAircraftIds } = get()
        return activePresets.size > 0 || highlightedAircraftIds.size > 0
      },
    }),
    { name: 'ramp-filter' }
  )
)
